import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Upload,
  Button,
  Space,
  List,
  Popconfirm,
  Typography,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import {
  DOCUMENT_AND_IMAGE_ACCEPT,
  beforeUploadDocumentOrImage,
  filterDocumentOrImageFileList,
} from "../../../utils/fileUpload";

const { TextArea } = Input;
const { Text } = Typography;

const CircularFormModal = ({
  visible,
  onClose,
  onSubmit,
  onDeleteFile,
  saving,
  deletingFileId,
  circular, // existing record when editing
  academicYears,
  terms,
  classes,
}) => {
  const [form] = Form.useForm();
  const [audience, setAudience] = useState("Whole School");
  const [fileList, setFileList] = useState([]);

  const isEditing = !!circular;

  useEffect(() => {
    if (visible) {
      if (isEditing) {
        form.setFieldsValue({
          title: circular.title,
          description: circular.description,
          audience: circular.audience,
          acadyrID: circular.acadyrID,
          termID: circular.termID,
          classIDs: circular.classes?.map((c) => c.id) || [],
        });
        setAudience(circular.audience);
      } else {
        form.resetFields();
        setAudience("Whole School");
      }
      setFileList([]);
    }
  }, [visible, circular, isEditing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append("title", values.title);
      if (values.description)
        formData.append("description", values.description);
      formData.append("audience", values.audience);
      formData.append("acadyrID", values.acadyrID);
      formData.append("termID", values.termID);

      if (values.audience === "Selected Classes") {
        (values.classIDs || []).forEach((id) =>
          formData.append("classIDs", id),
        );
      }

      fileList.forEach((f) => {
        const rawFile = f.originFileObj || f;
        formData.append("files", rawFile);
      });

      await onSubmit(formData, circular?.id);
    } catch {
      // validation failed
    }
  };

  const uploadProps = {
    beforeUpload: beforeUploadDocumentOrImage,
    multiple: true,
    fileList,
    onChange: ({ fileList: newList }) =>
      setFileList(filterDocumentOrImageFileList(newList)),
    accept: DOCUMENT_AND_IMAGE_ACCEPT,
  };

  return (
    <Modal
      title={isEditing ? "Edit Circular" : "New Circular"}
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEditing ? "Save Changes" : "Create"}
      confirmLoading={saving}
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ audience: "Whole School" }}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="e.g. Easter Break Reminder – Term I 2026" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Optional body text or notes" />
        </Form.Item>

        <Form.Item
          name="acadyrID"
          label="Academic Year"
          rules={[{ required: true, message: "Select an academic year" }]}
        >
          <Select
            placeholder="Select academic year"
            showSearch
            optionFilterProp="label"
          >
            {academicYears.map((y) => (
              <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                {y.acadyr}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="termID"
          label="Term"
          rules={[{ required: true, message: "Select a term" }]}
        >
          <Select placeholder="Select term" showSearch optionFilterProp="label">
            {terms.map((t) => (
              <Select.Option key={t.id} value={t.id} label={t.name}>
                {t.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="audience"
          label="Audience"
          rules={[{ required: true }]}
        >
          <Radio.Group
            onChange={(e) => {
              setAudience(e.target.value);
              if (e.target.value === "Whole School") {
                form.setFieldValue("classIDs", []);
              }
            }}
          >
            <Radio value="Whole School">Whole School</Radio>
            <Radio value="Selected Classes">Selected Classes</Radio>
          </Radio.Group>
        </Form.Item>

        {audience === "Selected Classes" && (
          <Form.Item
            name="classIDs"
            label="Classes"
            rules={[{ required: true, message: "Select at least one class" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select classes"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {classes.map((c) => (
                <Select.Option key={c.id} value={c.id} label={c.name}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {isEditing && circular?.files?.length > 0 && (
          <Form.Item label="Existing Files">
            <List
              size="small"
              dataSource={circular.files}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    <Popconfirm
                      title="Remove this file?"
                      onConfirm={() => onDeleteFile(circular.id, file.id)}
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        loading={deletingFileId === file.id}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>,
                  ]}
                >
                  <Space>
                    <PaperClipOutlined />
                    <Text>{file.filename}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Form.Item>
        )}

        <Form.Item label="Attach Files">
          <Upload {...uploadProps} listType="text">
            <Button icon={<UploadOutlined />}>Select Files</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CircularFormModal;
